import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type To, useNavigate, useSearchParams } from 'react-router';

import type { Product } from '~/api/products/types';

import { useCreateProduct, useUpdateProduct } from '~/api/products';
import { Blocker, Container, Header } from '~/components';
import { Form } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';

import type { CreateEditProductFormType } from './types';

import { sectionsStyle } from './CreateEditProductForm.css';
import { ProductBasicSection } from './sections/ProductBasicSection';
import { ProductDetailsSection } from './sections/ProductDetailsSection';

type Props =
  | {
      mode: 'create';
      product: null;
    }
  | {
      mode: 'edit';
      product: Product;
    };

export const CreateEditProductForm = ({ mode, product }: Props) => {
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct(product?.id ?? 0);

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'products']);

  const [searchParams] = useSearchParams();
  const redirectPath: To = {
    pathname: searchParams.get(SearchParamKeys.REDIRECT_PATH) ?? routes.products,
    search: searchParams.has(SearchParamKeys.REDIRECT_MODAL)
      ? qs({ [SearchParamKeys.OPEN_MODAL]: searchParams.get(SearchParamKeys.REDIRECT_MODAL) ?? '' })
      : undefined,
  };

  const form = useForm<CreateEditProductFormType>({
    defaultValues: {
      activityType: product?.activityType ?? 'CreativeGeneral',
      name: {
        nl: product?.name.nl ?? '',
        fr: product?.name.fr ?? '',
        en: product?.name.en ?? '',
      },
      price: product?.price ?? null,
      unit: product?.unit ?? null,
      productCategories: product?.productCategories.toSorted((a, b) => a.localeCompare(b)) ?? [],
    },
  });
  const {
    formState: { isDirty },
    handleSubmit,
  } = form;

  const isPending = createProductMutation.isPending || updateProductMutation.isPending;

  const onSubmit = handleSubmit((data) => {
    const { mutate } = mode === 'create' ? createProductMutation : updateProductMutation;

    mutate(
      {
        name: data.name,
        activityType: data.activityType,
        price: data.price,
        unit: data.unit,
        productCategories: data.productCategories,
      },
      {
        onSuccess: (product) => {
          toast.success({ description: t(`products:alerts.${mode === 'create' ? 'successCreated' : 'successUpdated'}`) });

          return navigate(redirectPath, {
            state: {
              ...bypassBlocker,
              ...(mode === 'edit' && { updatedProduct: product }),
            },
          });
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  return (
    <>
      <Blocker isBlocked={isDirty} message={t(`products:createEdit.blocker.${mode}`)} />

      <Header.Root>
        <Header.Content backTo={routes.products} title={t(`products:createEdit.title.${mode}`)} />

        <Header.Actions>
          <Header.Action icon="Save" loading={isPending} onClick={onSubmit}>
            {t('products:createEdit.submit.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Container>
        <Form autoComplete="off" onSubmit={onSubmit}>
          <div className={sectionsStyle}>
            <ProductBasicSection form={form} />
            <ProductDetailsSection form={form} product={product} />
          </div>
        </Form>
      </Container>
    </>
  );
};
